import React, { useCallback, useEffect, useState } from 'react';
import useApi from '../../hooks/useApi';
import Alert from '../common/Alert';
import CommunityIdentity from '../common/CommunityIdentity';

const FieldLabel = ({ children, help }) => (
  <span className="onboarding-field-label">
    {children}
    <span
      className="onboarding-help"
      tabIndex="0"
      role="note"
      aria-label={`${children}: ${help}`}
      data-help={help}
    >
      ?
    </span>
  </span>
);

export default function CommunityOnboarding({
  communityId,
  community,
  communityName,
  homesOnly = false,
}) {
  const { get, post, put, delete: remove } = useApi();
  const [data, setData] = useState({ levels: [], units: [], properties: [] }),
    [catalog, setCatalog] = useState({ services: [], plans: [] }),
    [entitled, setEntitled] = useState([]),
    [users, setUsers] = useState([]),
    [assignments, setAssignments] = useState([]),
    [residentRegister, setResidentRegister] = useState({
      content: [],
      totalElements: 0,
      totalPages: 0,
    }),
    [registerFilters, setRegisterFilters] = useState({
      search: '',
      structureUnitId: '',
      propertyId: '',
      relationship: '',
      accountStatus: '',
      mappingStatus: '',
      sortBy: 'resident',
      direction: 'asc',
      page: 0,
      size: 5,
    }),
    [propertyPage, setPropertyPage] = useState(0),
    [propertyPageSize, setPropertyPageSize] = useState(5),
    [propertyFilters, setPropertyFilters] = useState({
      property: '',
      type: '',
      status: '',
    }),
    [propertySort, setPropertySort] = useState({
      field: 'property',
      direction: 'asc',
    }),
    [tab, setTab] = useState(homesOnly ? 'properties' : 'structure'),
    [error, setError] = useState(''),
    [message, setMessage] = useState(''),
    [level, setLevel] = useState({
      levelOrder: 1,
      code: 'DIVISION',
      singularLabel: 'Division',
      pluralLabel: 'Divisions',
      requiredLevel: true,
    }),
    [unit, setUnit] = useState({
      structureLevelId: '',
      parentUnitId: '',
      code: '',
      name: '',
    }),
    [property, setProperty] = useState({
      structureUnitId: '',
      propertyNumber: '',
      displayName: '',
      propertyType: 'RESIDENTIAL',
    }),
    [assignment, setAssignment] = useState({
      userId: '',
      propertyId: '',
      relationshipType: 'RESIDENT',
      primaryProperty: true,
    }),
    [importFile, setImportFile] = useState(null),
    [importing, setImporting] = useState(false),
    [importInputKey, setImportInputKey] = useState(0);
  const load = useCallback(async () => {
    try {
      const [s, c, e, userPage, mapped] = await Promise.all([
        get('/api/community-structure', { params: { communityId } }),
        homesOnly ? Promise.resolve({ services: [], plans: [] }) : get('/api/subscriptions'),
        homesOnly
          ? Promise.resolve([])
          : get('/api/subscriptions/effective', { params: { communityId } }),
        get('/api/admin/users/search', {
          params: { communityId, page: 0, size: 100 },
        }),
        get('/api/community-structure/assignments', {
          params: { communityId },
        }),
      ]);
      setData(s);
      setCatalog(c);
      setEntitled(Array.from(e || []));
      setUsers(userPage.content || []);
      setAssignments(mapped || []);
      setError('');
    } catch (x) {
      setError(x.message);
    }
  }, [get, communityId, homesOnly]);
  useEffect(() => {
    load();
  }, [load]);
  const loadResidentRegister = useCallback(async () => {
    if (tab !== 'properties') return;
    try {
      const result = await get('/api/community-structure/resident-register', {
        params: {
          communityId,
          ...registerFilters,
          structureUnitId: registerFilters.structureUnitId || undefined,
          propertyId: registerFilters.propertyId || undefined,
          relationship: registerFilters.relationship || undefined,
          accountStatus: registerFilters.accountStatus || undefined,
          mappingStatus: registerFilters.mappingStatus || undefined,
          search: registerFilters.search || undefined,
        },
      });
      setResidentRegister(result);
    } catch (requestError) {
      setError(requestError.message);
    }
  }, [get, communityId, tab, registerFilters]);
  useEffect(() => {
    loadResidentRegister();
  }, [loadResidentRegister]);
  const save = async (path, body, reset) => {
    try {
      const { id, ...values } = body;
      const payload = {
        ...values,
        communityId,
        structureLevelId: values.structureLevelId
          ? Number(values.structureLevelId)
          : undefined,
        parentUnitId: values.parentUnitId
          ? Number(values.parentUnitId)
          : null,
        structureUnitId: values.structureUnitId
          ? Number(values.structureUnitId)
          : null,
        active: values.active !== false,
      };
      if (id) await put(`${path}/${id}`, payload);
      else await post(path, payload);
      reset();
      setMessage(id ? 'Configuration updated.' : 'Configuration added.');
      load();
    } catch (x) {
      setError(x.message);
    }
  };
  const deleteItem = async (path, id, label) => {
    if (
      !window.confirm(
        `Delete ${label}? Items already used by residents or finance cannot be deleted.`
      )
    )
      return;
    try {
      await remove(`${path}/${id}`, { params: { communityId } });
      setMessage(`${label} deleted.`);
      load();
    } catch (x) {
      setError(x.message);
    }
  };
  const downloadTemplate = async () => {
    try {
      const workbook = await get(
        '/api/community-structure/properties/import-template.xlsx',
        { params: { communityId }, responseType: 'blob' }
      );
      const url = URL.createObjectURL(
        new Blob([workbook], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(communityName || 'community')
        .replace(/[^a-z0-9]+/gi, '-')
        .toLowerCase()}-property-import.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (x) {
      setError(x.message);
    }
  };
  const saveServices = async () => {
    const disabledDefaults = catalog.services
      ?.filter(
        (service) =>
          service.core_service && !entitled.includes(service.service_code)
      )
      .map((service) => service.name);
    if (
      disabledDefaults?.length &&
      !window.confirm(
        `Disable ${disabledDefaults.join(
          ', '
        )}? These are enabled by default because they support basic community administration. Users will lose access to the disabled modules until you enable them again.`
      )
    ) {
      return;
    }
    try {
      await put('/api/subscriptions/entitlements', {
        communityId,
        services: entitled,
        source: 'CUSTOM',
        effectiveFrom: new Date().toISOString(),
        effectiveTo: null,
      });
      setMessage('Subscribed services updated.');
      load();
    } catch (x) {
      setError(x.message);
    }
  };
  const assignProperty = async (event) => {
    event.preventDefault();
    try {
      await post('/api/community-structure/assignments', {
        ...assignment,
        userId: Number(assignment.userId),
        propertyId: Number(assignment.propertyId),
      });
      setMessage('Resident mapped to the canonical property.');
      setAssignment({ ...assignment, userId: '', propertyId: '' });
      load();
      loadResidentRegister();
    } catch (x) {
      setError(x.message);
    }
  };
  const editAssignment = (item) => {
    setAssignment({
      userId: String(item.user_id),
      propertyId: String(item.property_id),
      relationshipType: item.relationship_type,
      primaryProperty: Boolean(item.primary_property),
    });
  };
  const deleteAssignment = async (item) => {
    if (
      !window.confirm(
        `Remove ${item.resident_name}'s mapping to ${item.property_name}?`
      )
    )
      return;
    try {
      await remove('/api/community-structure/assignments', {
        params: {
          communityId,
          userId: item.user_id,
          propertyId: item.property_id,
        },
      });
      setMessage('Resident-property mapping removed.');
      load();
      loadResidentRegister();
    } catch (x) {
      setError(x.message);
    }
  };
  const changeRegisterFilter = (field, value) =>
    setRegisterFilters((current) => ({ ...current, [field]: value, page: 0 }));
  const sortRegister = (field) =>
    setRegisterFilters((current) => ({
      ...current,
      sortBy: field,
      direction:
        current.sortBy === field && current.direction === 'asc' ? 'desc' : 'asc',
      page: 0,
    }));
  const exportResidentRegister = async (format) => {
    try {
      const body = await get(
        `/api/community-structure/resident-register.${format}`,
        {
          params: {
            communityId,
            ...registerFilters,
            page: undefined,
            size: undefined,
            structureUnitId: registerFilters.structureUnitId || undefined,
            propertyId: registerFilters.propertyId || undefined,
            relationship: registerFilters.relationship || undefined,
            accountStatus: registerFilters.accountStatus || undefined,
            mappingStatus: registerFilters.mappingStatus || undefined,
            search: registerFilters.search || undefined,
          },
          responseType: 'blob',
        }
      );
      const url = URL.createObjectURL(body);
      const link = document.createElement('a');
      link.href = url;
      link.download = `resident-configuration-register.${format}`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (requestError) {
      setError(requestError.message);
    }
  };
  const togglePropertySort = (field) => {
    setPropertySort((current) => ({
      field,
      direction:
        current.field === field && current.direction === 'asc' ? 'desc' : 'asc',
    }));
    setPropertyPage(0);
  };
  const filteredProperties = data.properties
    .filter((item) => {
      const propertyText = `${item.display_name || ''} ${item.property_number || ''}`.toLowerCase();
      return (
        propertyText.includes(propertyFilters.property.toLowerCase()) &&
        (!propertyFilters.type || item.property_type === propertyFilters.type) &&
        (!propertyFilters.status ||
          (propertyFilters.status === 'ACTIVE') === Boolean(item.active))
      );
    })
    .sort((left, right) => {
      const value = (item) =>
        propertySort.field === 'type'
          ? item.property_type
          : propertySort.field === 'status'
            ? item.active
              ? 'ACTIVE'
              : 'INACTIVE'
            : item.display_name || item.property_number;
      const compared = String(value(left) || '').localeCompare(
        String(value(right) || ''),
        undefined,
        { numeric: true, sensitivity: 'base' }
      );
      return propertySort.direction === 'asc' ? compared : -compared;
    });
  const propertyPageCount = Math.max(
    1,
    Math.ceil(filteredProperties.length / propertyPageSize)
  );
  const visibleProperties = filteredProperties.slice(
    propertyPage * propertyPageSize,
    propertyPage * propertyPageSize + propertyPageSize
  );
  const importProperties = async () => {
    if (!importFile) {
      setError('Choose a CSV or Excel (.xlsx) property sheet first.');
      return;
    }
    const form = new FormData();
    form.append('file', importFile);
    setImporting(true);
    try {
      const result = await post(
        `/api/community-structure/properties/import?communityId=${communityId}`,
        form
      );
      setMessage(`${result.imported} properties imported or updated.`);
      setImportFile(null);
      setImportInputKey((value) => value + 1);
      load();
    } catch (x) {
      setError(x.message);
    } finally {
      setImporting(false);
    }
  };
  return (
    <div className="engagement-page community-onboarding">
      <div className="engagement-heading">
        <div>
          <span>Community setup</span>
          <h2>{homesOnly ? 'Homes & resident configuration' : 'Layout, homes & enabled services'}</h2>
          <p>
            {homesOnly
              ? 'Manage this community’s homes and verify how registered residents are mapped to them.'
              : 'Use this only when onboarding or changing a community. Define how addresses are organised, add its homes or units, and choose the services included in its subscription.'}
          </p>
        </div>
      </div>
      {error && <Alert type="danger" message={error} />}{' '}
      {message && (
        <Alert
          type="success"
          message={message}
          onClose={() => setMessage('')}
        />
      )}
      <div className="alert alert-primary d-flex flex-column gap-1" role="status">
        <strong>Configuring</strong>
        <CommunityIdentity
          community={community || { name: communityName || `Community #${communityId}`, displayName: communityName }}
          size="sm"
        />
        <span>
          Every layout, home and enabled service saved below is assigned only
          to this community.
        </span>
      </div>
      {!homesOnly && <div className="alert alert-light border">
        <strong>Example: Tulsikunj Society</strong>
        <div className="mt-1">
          Address layout: <b>Division</b> → actual divisions <b>A, B and C</b>.
          Homes &amp; assignments: add <b>A-101, A-102, B-101</b>, then map
          residents to those homes. Enabled services: select the modules this
          society has purchased, such as Visitors, Amenities and Community
          Finance.
        </div>
        <small className="text-muted">
          A smaller community without divisions can use one level named
          “Community” and add homes directly beneath it.
        </small>
      </div>}
      {!homesOnly && <nav className="nav nav-pills gap-2 mb-3">
        <button
          className={`nav-link ${tab === 'structure' ? 'active' : ''}`}
          onClick={() => setTab('structure')}
        >
          Address layout
        </button>
        <button
          className={`nav-link ${tab === 'properties' ? 'active' : ''}`}
          onClick={() => setTab('properties')}
        >
          Homes &amp; assignments
        </button>
        <button
          className={`nav-link ${tab === 'services' ? 'active' : ''}`}
          onClick={() => setTab('services')}
        >
          Enabled services
        </button>
      </nav>}
      {tab === 'structure' && (
        <div className="settings-grid">
          <section className="engagement-list">
            <header>
              <div>
                <h3>Address levels</h3>
                <p>
                  Define the words this community uses, for example Division,
                  Wing, Block or Tower. A community with no hierarchy can use
                  one level only.
                </p>
              </div>
            </header>
            <form
              className="settings-grid"
              onSubmit={(e) => {
                e.preventDefault();
                save('/api/community-structure/levels', level, () =>
                  setLevel({
                    levelOrder: data.levels.length + 1,
                    code: '',
                    singularLabel: '',
                    pluralLabel: '',
                    requiredLevel: true,
                    active: true,
                  })
                );
              }}
            >
              <label>
                <FieldLabel help="The display order of this address level. Use 1 for the broadest level, then 2, 3 and so on.">
                  Sequence
                </FieldLabel>
                <input
                  className="form-control"
                  type="number"
                  min="1"
                  value={level.levelOrder}
                  onChange={(e) =>
                    setLevel({ ...level, levelOrder: Number(e.target.value) })
                  }
                />
              </label>
              <label>
                <FieldLabel help="A short, permanent system code such as DIVISION or WING. Use uppercase letters and avoid changing it after setup.">
                  Internal code
                </FieldLabel>
                <input
                  className="form-control"
                  value={level.code}
                  onChange={(e) => setLevel({ ...level, code: e.target.value })}
                />
              </label>
              <label>
                <FieldLabel help="The word residents see for one item, for example Division, Wing, Block or Tower.">
                  Singular label
                </FieldLabel>
                <input
                  className="form-control"
                  value={level.singularLabel}
                  onChange={(e) =>
                    setLevel({ ...level, singularLabel: e.target.value })
                  }
                />
              </label>
              <label>
                <FieldLabel help="The word residents see for multiple items, for example Divisions, Wings, Blocks or Towers.">
                  Plural label
                </FieldLabel>
                <input
                  className="form-control"
                  value={level.pluralLabel}
                  onChange={(e) =>
                    setLevel({ ...level, pluralLabel: e.target.value })
                  }
                />
              </label>
              <label className="form-check align-self-end mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={level.active !== false}
                  onChange={(e) =>
                    setLevel({ ...level, active: e.target.checked })
                  }
                />{' '}
                <FieldLabel help="Turn this off to hide the level from new selections without deleting existing data.">
                  Active
                </FieldLabel>
              </label>
              <div className="d-flex gap-2">
                <button className="btn btn-primary">
                  {level.id ? 'Update level' : 'Add level'}
                </button>
                {level.id && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() =>
                      setLevel({
                        levelOrder: data.levels.length + 1,
                        code: '',
                        singularLabel: '',
                        pluralLabel: '',
                        requiredLevel: true,
                        active: true,
                      })
                    }
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
            {data.levels.map((x) => (
              <div
                key={x.id}
                className="d-flex justify-content-between align-items-center gap-2 border-top py-2"
              >
                <span>
                  <strong>
                    {x.level_order}. {x.singular_label}
                  </strong>{' '}
                  <small className="text-muted">{x.code}</small>
                  {!x.active && (
                    <span className="badge text-bg-secondary ms-2">
                      Inactive
                    </span>
                  )}
                </span>
                <span className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={() =>
                      setLevel({
                        id: x.id,
                        levelOrder: x.level_order,
                        code: x.code,
                        singularLabel: x.singular_label,
                        pluralLabel: x.plural_label,
                        requiredLevel: x.required_level,
                        active: x.active,
                      })
                    }
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() =>
                      deleteItem(
                        '/api/community-structure/levels',
                        x.id,
                        x.singular_label
                      )
                    }
                  >
                    Delete
                  </button>
                </span>
              </div>
            ))}
          </section>
          <section className="engagement-list">
            <header>
               <div>
                 <h3>Actual divisions, wings or blocks</h3>
                 <p>
                   Add the real names under the levels above, such as Wing A,
                   Wing B or Tower 1. Parent is needed only for nested layouts.
                 </p>
               </div>
            </header>
            <form
              className="settings-grid"
              onSubmit={(e) => {
                e.preventDefault();
                save('/api/community-structure/units', unit, () =>
                  setUnit({
                    structureLevelId: '',
                    parentUnitId: '',
                    code: '',
                    name: '',
                    active: true,
                  })
                );
              }}
            >
              <label>
                <FieldLabel help="Choose the address level this real unit belongs to, such as Division or Wing.">
                  Level
                </FieldLabel>
                <select
                  className="form-select"
                  required
                  value={unit.structureLevelId}
                  onChange={(e) =>
                    setUnit({ ...unit, structureLevelId: e.target.value })
                  }
                >
                  <option value="">Select</option>
                  {data.levels.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.singular_label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <FieldLabel help="Choose the larger unit containing this one. Leave None for a top-level unit such as Division A.">
                  Parent
                </FieldLabel>
                <select
                  className="form-select"
                  value={unit.parentUnitId}
                  onChange={(e) =>
                    setUnit({ ...unit, parentUnitId: e.target.value })
                  }
                >
                  <option value="">None</option>
                  {data.units.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <FieldLabel help="A unique compact code used in imports and references, for example A, B or WING-A.">
                  Short code
                </FieldLabel>
                <input
                  className="form-control"
                  required
                  value={unit.code}
                  onChange={(e) => setUnit({ ...unit, code: e.target.value })}
                />
              </label>
              <label>
                <FieldLabel help="The resident-facing name of the actual division, wing, block or tower, for example Division A.">
                  Name
                </FieldLabel>
                <input
                  className="form-control"
                  required
                  value={unit.name}
                  onChange={(e) => setUnit({ ...unit, name: e.target.value })}
                />
              </label>
              <label className="form-check align-self-end mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={unit.active !== false}
                  onChange={(e) =>
                    setUnit({ ...unit, active: e.target.checked })
                  }
                />{' '}
                <FieldLabel help="Turn this off to prevent new homes from being assigned here while preserving existing records.">
                  Active
                </FieldLabel>
              </label>
              <div className="d-flex gap-2">
                <button className="btn btn-primary">
                  {unit.id ? 'Update structure unit' : 'Add structure unit'}
                </button>
                {unit.id && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() =>
                      setUnit({
                        structureLevelId: '',
                        parentUnitId: '',
                        code: '',
                        name: '',
                        active: true,
                      })
                    }
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
            {data.units.map((x) => (
              <div
                key={x.id}
                className="d-flex justify-content-between align-items-center gap-2 border-top py-2"
              >
                <span>
                  <strong>{x.name}</strong>{' '}
                  <small className="text-muted">{x.code}</small>
                  {!x.active && (
                    <span className="badge text-bg-secondary ms-2">
                      Inactive
                    </span>
                  )}
                </span>
                <span className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={() =>
                      setUnit({
                        id: x.id,
                        structureLevelId: x.structure_level_id,
                        parentUnitId: x.parent_unit_id || '',
                        code: x.code,
                        name: x.name,
                        active: x.active,
                      })
                    }
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() =>
                      deleteItem(
                        '/api/community-structure/units',
                        x.id,
                        x.name
                      )
                    }
                  >
                    Delete
                  </button>
                </span>
              </div>
            ))}
          </section>
        </div>
      )}
      {tab === 'properties' && (
        <section className="engagement-list">
          <header>
            <div>
               <h3>Homes, flats, shops or units</h3>
              <p>
                Add the individual premises where residents belong. You can
                then map each user to the correct home. CSV import is available
                for larger communities.
              </p>
            </div>
          </header>
          <form
            className="settings-grid"
            onSubmit={(e) => {
              e.preventDefault();
                save('/api/community-structure/properties', property, () =>
                  setProperty({
                    structureUnitId: '',
                    propertyNumber: '',
                    displayName: '',
                    propertyType: 'RESIDENTIAL',
                    active: true,
                  })
                );
            }}
          >
            <label>
              <FieldLabel help="The division, wing, block or tower containing this home. Choose Community root when no hierarchy is used.">
                Structure unit
              </FieldLabel>
              <select
                className="form-select"
                value={property.structureUnitId}
                onChange={(e) =>
                  setProperty({ ...property, structureUnitId: e.target.value })
                }
              >
                <option value="">Community root</option>
                {data.units.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <FieldLabel help="The official flat, house, shop or unit number, for example 101 or A-25.">
                Property number
              </FieldLabel>
              <input
                className="form-control"
                required
                value={property.propertyNumber}
                onChange={(e) =>
                  setProperty({ ...property, propertyNumber: e.target.value })
                }
              />
            </label>
            <label>
              <FieldLabel help="A friendly name shown in lists and reports. If left blank, the property number is used.">
                Display name
              </FieldLabel>
              <input
                className="form-control"
                value={property.displayName}
                onChange={(e) =>
                  setProperty({ ...property, displayName: e.target.value })
                }
              />
            </label>
              <label>
                <FieldLabel help="Classify the premises so reports and future billing rules can distinguish homes, shops and other units.">
                  Type
                </FieldLabel>
              <select
                className="form-select"
                value={property.propertyType}
                onChange={(e) =>
                  setProperty({ ...property, propertyType: e.target.value })
                }
              >
                {['RESIDENTIAL', 'COMMERCIAL', 'VILLA', 'SHOP', 'OTHER'].map(
                  (x) => (
                    <option key={x}>{x}</option>
                  )
                )}
                </select>
              </label>
            <label className="form-check align-self-end mb-2">
              <input
                className="form-check-input"
                type="checkbox"
                checked={property.active !== false}
                onChange={(e) =>
                  setProperty({ ...property, active: e.target.checked })
                }
              />{' '}
              <FieldLabel help="Turn this off when a property should no longer accept new resident assignments, without deleting its history.">
                Active
              </FieldLabel>
            </label>
            <div className="d-flex gap-2">
              <button className="btn btn-primary">
                {property.id ? 'Update property' : 'Add property'}
              </button>
              {property.id && (
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() =>
                    setProperty({
                      structureUnitId: '',
                      propertyNumber: '',
                      displayName: '',
                      propertyType: 'RESIDENTIAL',
                      active: true,
                    })
                  }
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
          <div className="settings-grid mt-3">
            <label>
              Filter property
              <input
                className="form-control"
                placeholder="Number or display name"
                value={propertyFilters.property}
                onChange={(event) => {
                  setPropertyFilters({
                    ...propertyFilters,
                    property: event.target.value,
                  });
                  setPropertyPage(0);
                }}
              />
            </label>
            <label>
              Filter type
              <select
                className="form-select"
                value={propertyFilters.type}
                onChange={(event) => {
                  setPropertyFilters({
                    ...propertyFilters,
                    type: event.target.value,
                  });
                  setPropertyPage(0);
                }}
              >
                <option value="">All types</option>
                {['RESIDENTIAL', 'COMMERCIAL', 'VILLA', 'SHOP', 'OTHER'].map(
                  (value) => (
                    <option key={value}>{value}</option>
                  )
                )}
              </select>
            </label>
            <label>
              Filter status
              <select
                className="form-select"
                value={propertyFilters.status}
                onChange={(event) => {
                  setPropertyFilters({
                    ...propertyFilters,
                    status: event.target.value,
                  });
                  setPropertyPage(0);
                }}
              >
                <option value="">All statuses</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </label>
            <label>
              Rows per page
              <select
                className="form-select"
                value={propertyPageSize}
                onChange={(event) => {
                  setPropertyPageSize(Number(event.target.value));
                  setPropertyPage(0);
                }}
              >
                {[5, 10, 25, 50].map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="table-responsive mt-3">
            <table className="table">
              <thead>
                <tr>
                  <th>
                    <button type="button" className="btn btn-link p-0" onClick={() => togglePropertySort('property')}>
                      Property {propertySort.field === 'property' ? (propertySort.direction === 'asc' ? '↑' : '↓') : ''}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="btn btn-link p-0" onClick={() => togglePropertySort('type')}>
                      Type {propertySort.field === 'type' ? (propertySort.direction === 'asc' ? '↑' : '↓') : ''}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="btn btn-link p-0" onClick={() => togglePropertySort('status')}>
                      Status {propertySort.field === 'status' ? (propertySort.direction === 'asc' ? '↑' : '↓') : ''}
                    </button>
                  </th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleProperties.map((x) => (
                  <tr key={x.id}>
                    <td>{x.display_name}</td>
                    <td>{x.property_type}</td>
                    <td>{x.active ? 'Active' : 'Inactive'}</td>
                    <td>
                      <div className="d-flex justify-content-end gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() =>
                            setProperty({
                              id: x.id,
                              structureUnitId: x.structure_unit_id || '',
                              propertyNumber: x.property_number,
                              displayName: x.display_name,
                              propertyType: x.property_type,
                              active: x.active,
                            })
                          }
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() =>
                            deleteItem(
                              '/api/community-structure/properties',
                              x.id,
                              x.display_name || x.property_number
                            )
                          }
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!visibleProperties.length && (
                  <tr>
                    <td colSpan="4" className="text-center text-muted">
                      No properties match the selected filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <small className="text-muted">
              {filteredProperties.length
                ? `${propertyPage * propertyPageSize + 1}–${Math.min(
                    (propertyPage + 1) * propertyPageSize,
                    filteredProperties.length
                  )} of ${filteredProperties.length}`
                : '0 properties'}
            </small>
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={propertyPage === 0}
                onClick={() => setPropertyPage((value) => value - 1)}
              >
                Previous
              </button>
              <span>
                Page {Math.min(propertyPage + 1, propertyPageCount)} of{' '}
                {propertyPageCount}
              </span>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={propertyPage + 1 >= propertyPageCount}
                onClick={() => setPropertyPage((value) => value + 1)}
              >
                Next
              </button>
            </div>
          </div>
          <div className="mt-3 p-3 border rounded">
            <strong>Bulk import properties</strong>
            <p className="text-muted mb-2">
              CSV or Excel (.xlsx) columns: structure_unit_code,
              property_number, display_name, property_type. Create matching
              structure-unit codes first (for example A, B, C and D), then
              upload the file.
            </p>
            <button
              type="button"
              className="btn btn-outline-primary mb-2"
              onClick={downloadTemplate}
            >
              Download populated Excel template
            </button>
            <input
              key={importInputKey}
              className="form-control"
              type="file"
              accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(event) =>
                setImportFile(event.target.files?.[0] || null)
              }
            />
            <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
              <button
                type="button"
                className="btn btn-primary"
                disabled={!importFile || importing}
                onClick={importProperties}
              >
                {importing ? 'Uploading…' : 'Upload properties'}
              </button>
              <small className="text-muted">
                {importFile
                  ? `Selected: ${importFile.name}`
                  : 'No property sheet selected.'}
              </small>
            </div>
          </div>
          <hr />
          <h3>Map existing residents</h3>
          <p>
            Link an existing account to its canonical home after properties are
            imported. For example, if Amit already has an account with legacy
            address “A / 25”, map Amit to the imported property “A-25”. This
            mapping controls that resident’s bills, receipts and property-scoped
            services. A resident may have multiple mappings, but only one should
            normally be marked as primary.
          </p>
          <form className="settings-grid" onSubmit={assignProperty}>
            <label>
              <FieldLabel help="Select the existing user account that should be linked to a home or unit.">
                Resident
              </FieldLabel>
              <select
                className="form-select"
                required
                value={assignment.userId}
                onChange={(e) =>
                  setAssignment({ ...assignment, userId: e.target.value })
                }
              >
                <option value="">Select resident</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name} · {user.email}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <FieldLabel help="Select the home, flat, shop or unit occupied or owned by this resident.">
                Property
              </FieldLabel>
              <select
                className="form-select"
                required
                value={assignment.propertyId}
                onChange={(e) =>
                  setAssignment({ ...assignment, propertyId: e.target.value })
                }
              >
                <option value="">Select property</option>
                {data.properties
                  .filter((x) => x.active)
                  .map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.display_name}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              <FieldLabel help="Describe how the selected person is connected to the property: owner, tenant, resident or family member.">
                Relationship
              </FieldLabel>
              <select
                className="form-select"
                value={assignment.relationshipType}
                onChange={(e) =>
                  setAssignment({
                    ...assignment,
                    relationshipType: e.target.value,
                  })
                }
              >
                <option>OWNER</option>
                <option>TENANT</option>
                <option>RESIDENT</option>
                <option>FAMILY</option>
              </select>
            </label>
            <label className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                checked={assignment.primaryProperty}
                onChange={(e) =>
                  setAssignment({
                    ...assignment,
                    primaryProperty: e.target.checked,
                  })
                }
              />{' '}
              <FieldLabel help="Mark this when this is the user's main address. A user with more than one property should have only one primary property.">
                Primary property
              </FieldLabel>
            </label>
            <button className="btn btn-primary">Map resident</button>
          </form>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-4">
            <div>
              <h4 className="mb-1">Resident configuration register</h4>
              <p className="text-muted mb-0">
                Verify every registered resident, including accounts that have
                not yet been mapped to a configured home.
              </p>
            </div>
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-outline-primary" onClick={() => exportResidentRegister('csv')}>
                Export CSV
              </button>
              <button type="button" className="btn btn-outline-primary" onClick={() => exportResidentRegister('pdf')}>
                Export PDF
              </button>
            </div>
          </div>
          <div className="settings-grid mt-3">
            <label>
              Search resident or property
              <input
                className="form-control"
                value={registerFilters.search}
                placeholder="Name, email or property"
                onChange={(event) => changeRegisterFilter('search', event.target.value)}
              />
            </label>
            <label>
              Structure
              <select className="form-select" value={registerFilters.structureUnitId} onChange={(event) => changeRegisterFilter('structureUnitId', event.target.value)}>
                <option value="">All structures</option>
                {data.units.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </select>
            </label>
            <label>
              Property
              <select className="form-select" value={registerFilters.propertyId} onChange={(event) => changeRegisterFilter('propertyId', event.target.value)}>
                <option value="">All properties</option>
                {data.properties.map((item) => <option key={item.id} value={item.id}>{item.display_name || item.property_number}</option>)}
              </select>
            </label>
            <label>
              Relationship
              <select className="form-select" value={registerFilters.relationship} onChange={(event) => changeRegisterFilter('relationship', event.target.value)}>
                <option value="">All relationships</option>
                {['OWNER', 'TENANT', 'RESIDENT', 'FAMILY'].map((value) => <option key={value}>{value}</option>)}
              </select>
            </label>
            <label>
              Account status
              <select className="form-select" value={registerFilters.accountStatus} onChange={(event) => changeRegisterFilter('accountStatus', event.target.value)}>
                <option value="">All account statuses</option>
                {['ACTIVE', 'PENDING', 'DISABLED'].map((value) => <option key={value}>{value}</option>)}
              </select>
            </label>
            <label>
              Mapping status
              <select className="form-select" value={registerFilters.mappingStatus} onChange={(event) => changeRegisterFilter('mappingStatus', event.target.value)}>
                <option value="">Mapped and unmapped</option>
                <option value="MAPPED">Mapped</option>
                <option value="UNMAPPED">Unmapped</option>
              </select>
            </label>
            <label>
              Rows per page
              <select className="form-select" value={registerFilters.size} onChange={(event) => changeRegisterFilter('size', Number(event.target.value))}>
                {[5, 10, 25, 50].map((value) => <option key={value} value={value}>{value}</option>)}
              </select>
            </label>
          </div>
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  {[
                    ['resident', 'Resident'],
                    ['structure', 'Structure'],
                    ['property', 'Property'],
                    ['relationship', 'Relationship'],
                    ['status', 'Account'],
                    ['mapping', 'Mapping'],
                  ].map(([field, label]) => (
                    <th key={field}>
                      <button type="button" className="btn btn-link p-0" onClick={() => sortRegister(field)}>
                        {label} {registerFilters.sortBy === field ? (registerFilters.direction === 'asc' ? '↑' : '↓') : ''}
                      </button>
                    </th>
                  ))}
                  <th>Primary</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {residentRegister.content.map((item, index) => (
                  <tr key={`${item.user_id}-${item.property_id || 'unmapped'}-${index}`}>
                    <td>
                      {item.resident_name}
                      <small className="d-block text-muted">
                        {item.resident_email}
                      </small>
                    </td>
                    <td>{item.structure_unit_name || 'Community root'}</td>
                    <td>{item.property_name || item.property_number || 'Not mapped'}</td>
                    <td>{item.relationship_type || '—'}</td>
                    <td>
                      {item.account_status}
                      <small className="d-block text-muted">
                        {item.approved ? 'Approved' : 'Awaiting approval'}
                      </small>
                    </td>
                    <td>
                      <span className={`badge ${item.mapping_status === 'MAPPED' ? 'text-bg-success' : 'text-bg-warning'}`}>
                        {item.mapping_status}
                      </span>
                    </td>
                    <td>{item.primary_property ? 'Yes' : 'No'}</td>
                    <td>
                      <div className="d-flex justify-content-end gap-2">
                        {item.mapping_status === 'MAPPED' ? (
                          <>
                            <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => editAssignment(item)}>Edit</button>
                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => deleteAssignment(item)}>Remove</button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => setAssignment((current) => ({ ...current, userId: String(item.user_id) }))}
                          >
                            Map resident
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {!residentRegister.content.length && (
                  <tr>
                    <td colSpan="8" className="text-center text-muted">
                      No registered residents match these filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <small className="text-muted">{residentRegister.totalElements || 0} registered resident records</small>
            <div className="d-flex align-items-center gap-2">
              <button type="button" className="btn btn-sm btn-outline-secondary" disabled={registerFilters.page === 0} onClick={() => setRegisterFilters((current) => ({ ...current, page: current.page - 1 }))}>Previous</button>
              <span>Page {residentRegister.totalPages ? registerFilters.page + 1 : 0} of {residentRegister.totalPages || 0}</span>
              <button type="button" className="btn btn-sm btn-outline-secondary" disabled={registerFilters.page + 1 >= residentRegister.totalPages} onClick={() => setRegisterFilters((current) => ({ ...current, page: current.page + 1 }))}>Next</button>
            </div>
          </div>
        </section>
      )}
      {tab === 'services' && (
        <section className="engagement-list">
          <header>
            <div>
              <h3>Services enabled for this community</h3>
              <p>
                This controls which modules the community has purchased and
                can see. Community Finance remains hidden until it is enabled
                here; permissions inside an enabled module are managed
                separately through Access Control.
              </p>
              <p className="mb-0">
                <strong>Billing workflow:</strong> enable Community Finance for{' '}
                {communityName || 'this community'}, then its Community Admin
                can configure funds and payment accounts and create maintenance,
                AMC or event-specific collection requests.
              </p>
              <p className="mb-0 mt-2 text-muted">
                Services marked <strong>Enabled by default</strong> support the
                basic platform experience. A Super Admin may still turn them
                off for a community, but doing so removes that module from its
                users until it is enabled again.
              </p>
            </div>
          </header>
          <div className="settings-grid">
            {catalog.services?.map((s) => (
              <label key={s.service_code} className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={entitled.includes(s.service_code)}
                  onChange={(e) =>
                    setEntitled((v) =>
                      e.target.checked
                        ? [...new Set([...v, s.service_code])]
                        : v.filter((x) => x !== s.service_code)
                    )
                  }
                />
                <span>
                  <strong>
                    {s.name}{' '}
                    <span
                      className="onboarding-help"
                      tabIndex="0"
                      role="note"
                      aria-label={`${s.name}: ${
                        s.description || 'Controls access to this module.'
                      }`}
                      data-help={
                        s.description || 'Controls access to this module.'
                      }
                    >
                      ?
                    </span>
                  </strong>
                  {s.core_service && (
                    <span className="badge text-bg-light ms-2">
                      Enabled by default
                    </span>
                  )}
                  <small className="d-block text-muted">{s.description}</small>
                </span>
              </label>
            ))}
          </div>
          <button className="btn btn-primary mt-3" onClick={saveServices}>
            Save enabled services
          </button>
        </section>
      )}
    </div>
  );
}
